import axios from "axios";
import { tokenStore } from "./tokenStore";

export const apiClient = axios.create({
  baseURL: "/api",
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request Interceptor: Attach Access Token from in-memory tokenStore
apiClient.interceptors.request.use(async (config) => {
  const token = tokenStore.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshTokenPromise = null;

// A browser holds one session cookie, shared by all its tabs. Signing in as
// another account in one tab, or signing out there, switches or ends the
// session every other tab renews with, while those tabs still show the old
// account's pages (the admin page then answered "This endpoint is for
// TickAndPass platform admins.", 23 Oct 2026). Each renewal tells the
// listeners whose session it is, so a tab can follow (hooks/useAuth.jsx).
const sessionListeners = new Set();

/**
 * Calls `listener(user)` after each renewal: the session's user, null when
 * the server refused the renewal (there is no session any more), undefined
 * when it did not say whose it is. Returns the unsubscribe.
 */
export function onSessionRenewed(listener) {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

function announceSession(user) {
  sessionListeners.forEach((listener) => listener(user));
}

// Helper for silent session refresh via HttpOnly refresh cookie
export async function performSilentRefresh() {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = (async () => {
    try {
      const { data } = await axios.post(
        "/api/auth/refresh",
        {},
        { withCredentials: true },
      );
      const newAccessToken = data?.accessToken || data?.data?.accessToken;
      if (newAccessToken) {
        tokenStore.setAccessToken(newAccessToken);
        announceSession(data?.user ?? data?.data?.user);
        return newAccessToken;
      }
    } catch (err) {
      tokenStore.clearAccessToken();
      // Refused: no cookie, or the session was ended elsewhere. A network
      // failure says nothing about the session, so it is not announced.
      if (err?.response?.status === 401) announceSession(null);
      return null;
    } finally {
      refreshTokenPromise = null;
    }
    return null;
  })();

  return refreshTokenPromise;
}

// Response Interceptor: Auto-refresh on 401
apiClient.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      typeof response.data === "object" &&
      "data" in response.data
    ) {
      return {
        ...response,
        data: response.data.data,
        meta: response.data.meta,
      };
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/login") &&
      !originalRequest.url?.includes("/auth/refresh")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await performSilentRefresh();

        if (newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);
          return apiClient(originalRequest);
        } else {
          processQueue(new Error("Refresh failed"), null);
        }
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        tokenStore.clearAccessToken();
      } finally {
        isRefreshing = false;
      }
    }

    const apiError = error.response?.data?.error;
    const normalized = new Error(
      apiError?.message ??
        error.response?.data?.message ??
        error.message ??
        "Request failed",
    );
    normalized.code =
      apiError?.code ?? `ERR_HTTP_${error.response?.status ?? "UNKNOWN"}`;
    normalized.status = error.response?.status;
    normalized.details = apiError?.details;
    normalized.original = error;
    return Promise.reject(normalized);
  },
);
